import React, { useState, useEffect } from "react";
import { createSearchParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux'
import { Container, Modal, Alert } from "react-bootstrap";
import { emptyCart } from "actions/cart";
import { setGames } from "actions/auth";
import Analytics from "../analytics";
import * as Sentry from "@sentry/react";

import StripeService from "../services/stripe.service";
import AuthService from 'services/auth';
import { generateVoucherPdf } from 'services/voucherPdf.service';

import styles from "./CheckoutReturnStyles.module.scss";
import formStyles from "./Forms/FormStyles.module.scss";
import Login from "./Forms/Login";
import FormPage from "./Forms/FormPage";
import { getGameList, getAddons } from "../constants/GameListAdapter";
import DotLoader from 'components/Utlility/DotLoader';
import BackButton from './Forms/BackButton';
import presentIcon from '../images/icons/present.png';
import clockIcon from '../images/icons/clock.png';

export default function CheckoutReturn() {
    const [isLoading, setIsLoading] = useState(true);
    const [loadingText, setLoadingText] = useState("Loading...");
    const [purchaseError, setPurchaseError] = useState(false);
    const [products, setProducts] = useState([]);
    const [email, setEmail] = useState("");
    const [editEmail, setEditEmail] = useState("");
    const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
    const [emailUpdateStatus, setEmailUpdateStatus] = useState(""); // "success" | "error" | ""

    // View states
    const [currentView, setCurrentView] = useState("confirmation"); // confirmation, redeemed, voucher
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [missingGames, setMissingGames] = useState([]);

    const [isActivating, setIsActivating] = useState(false);
    const [productsToActivate, setProductsToActivate] = useState([]);
    const [activateMessage, setActivateMessage] = useState("");
    const [activateStatus, setActivateStatus] = useState("");

    const [showLoginModal, setShowLoginModal] = useState(false);
    const [copiedCode, setCopiedCode] = useState(null);
    const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

    const { isLoggedIn, user } = useSelector(state => state.auth);
    const { countryCode } = useSelector(state => state.pricing);
    const room = useSelector(state => state.room);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    var pollPaymentKeys = null;

    useEffect(() => {
        const paymentIntentId = new URLSearchParams(window.location.search).get("payment_intent");

        if (!paymentIntentId) {
            navigate("/checkout");
        }
        emptyCheckoutCart();
        setLoadingText("Verifying your payment, don't close this page...");

        pollPaymentKeys = setInterval(pollForPaymentKeys, 4000);

        return () => {
            clearInterval(pollPaymentKeys);
        }
    }, []);

    const pollForPaymentKeys = () => {
        const paymentIntentId = new URLSearchParams(window.location.search).get("payment_intent");

        StripeService.getPaymentKeys(paymentIntentId).then((res) => {
            clearInterval(pollPaymentKeys);
            setIsLoading(false);
            setProducts(res.data.orderProducts);
            setEmail(res.data.email || user?.email || "");
            setEditEmail(res.data.email || user?.email || "");
            assignProductsToActivate(res.data.orderProducts);

            if (!localStorage.getItem(`${paymentIntentId}_triggered`)) {
                Analytics.purchaseComplete(res.data.orderProducts, res.data.currency, res.data.total, paymentIntentId, res.data.coupon);
                localStorage.setItem(`${paymentIntentId}_triggered`, true);
            }
        }).catch((err) => {
            if (err.status === 500) {
                setPurchaseError(true);
                setIsLoading(false);
                Sentry.captureMessage(`Error verifying payment: ${err}`);
            }
        });
    }

    const emptyCheckoutCart = () => {
        dispatch(emptyCart());
    }

    const assignProductsToActivate = (products) => {
        let listOfCodes = [];
        products.forEach((product) => {
            // Include ALL codes for each product
            product.codes.forEach((code) => {
                listOfCodes.push(code);
            });
        })
        setProductsToActivate(listOfCodes);
    }

    const getTotalCodesCount = () => {
        return products.reduce((total, product) => total + product.codes.length, 0);
    }

    const truncateCode = (code) => {
        if (code.length > 16) {
            return code.substring(0, 8) + "..." + code.substring(code.length - 6);
        }
        return code;
    }

    const copyToClipboard = (code) => {
        navigator.clipboard.writeText(code);
        setCopiedCode(code);
        setTimeout(() => setCopiedCode(null), 2000);
    }

    const handleClose = () => {
        if (!isActivating) {
            setActivateMessage("");
            setActivateStatus(false);
            setShowConfirmationModal(false);
        }
    }

    const handleLoginClose = (loggedIn = false) => {
        setShowLoginModal(false);
        if (loggedIn) {
            clickActivate(true);
        }
    }

    const handlePlayNow = () => {
        if (!isLoggedIn) {
            setShowLoginModal(true);
        } else {
            checkMissingBaseGames();
        }
    }

    const checkMissingBaseGames = () => {
        if (productsToActivate.length > 0) {
            // Find addons in purchased products
            const purchasedAddons = products.map((p) => getAddons().find(g => g.productId === p.productId)).filter(x => x);
            // Find base games for each addon (games that have this addon in their addonIds)
            const baseGames = purchasedAddons.map((addon) =>
                getGameList().find(g => g.type === 'Game' && g.addonIds?.includes(addon.gameId))
            ).filter(x => x);
            const missingBaseGames = [...new Set(baseGames.filter(bg => !(products.find(p => p.productId === bg.productId) || user?.games.find(ug => ug.code === bg.gameId))))];
            setMissingGames(missingBaseGames);
            setShowConfirmationModal(true);
        }
    }

    const handleGiftVoucher = () => {
        if (productsToActivate.length > 0) {
            setIsActivating(true);
            AuthService.sendGiftVoucher(productsToActivate).then((response) => {
                setIsActivating(false);
                let message = response.message || response.message.toString();
                if (response.status === "Info") {
                    response.messageList.forEach((x) => {
                        message += `\n${x}`;
                    });
                }
                setActivateMessage(message);
                setActivateStatus(response.status);
                if (["Info", "Success"].includes(response.status)) {
                    setCurrentView("voucher");
                }
            }, (error) => {
                const messageError = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
                setIsActivating(false);
                setActivateMessage(messageError);
                setActivateStatus("Error");
            });
        }
    }

    const clickActivate = (fromLoginOrRegister = false) => {
        if (productsToActivate.length > 0) {
            if (fromLoginOrRegister) {
                setIsLoading(true);
            } else {
                setIsActivating(true);
            }

            AuthService.redeemMultipleCodes(productsToActivate).then((response) => {
                if (fromLoginOrRegister) {
                    setIsLoading(false);
                } else {
                    setIsActivating(false);
                }
                let message = response.message || response.message.toString();
                if (response.status == "Info") {
                    response.messageList.forEach((x) => {
                        message += `\n${x}`;
                    });
                }
                setActivateMessage(message);
                setActivateStatus(response.status);
                if (["Info", "Success"].includes(response.status)) {
                    setShowConfirmationModal(false);
                    setCurrentView("redeemed");
                    dispatch(setGames(response.games));
                    if (room.roomOpen) room.room.send("refresh_lobby_games", {});
                }
            }, (error) => {
                const messageError = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
                if (fromLoginOrRegister) {
                    setIsLoading(false);
                } else {
                    setIsActivating(false);
                }
                setActivateMessage(messageError);
                setActivateStatus("Error");
            });
        }
    }

    const handleUpdateEmail = async () => {
        const paymentIntentId = new URLSearchParams(window.location.search).get("payment_intent");
        if (!paymentIntentId || !editEmail) return;

        setIsUpdatingEmail(true);
        setEmailUpdateStatus("");
        try {
            await StripeService.updatePaymentIntentEmail(paymentIntentId, editEmail);
            setEmail(editEmail);
            setEmailUpdateStatus("success");
            setTimeout(() => setEmailUpdateStatus(""), 5000);
        } catch (error) {
            console.error("Failed to update email:", error);
            setEmailUpdateStatus("error");
        } finally {
            setIsUpdatingEmail(false);
        }
    }

    const handleDownloadPdf = async () => {
        setIsDownloadingPdf(true);
        try {
            await generateVoucherPdf(products, email);
        } catch (error) {
            console.error("Failed to generate PDF:", error);
        } finally {
            setIsDownloadingPdf(false);
        }
    }

    // Loading State
    if (isLoading) {
        return (
            <Container>
                <div className={styles.loadingContainer}>
                    <DotLoader size="medium" variant="dark" />
                    <p>{loadingText}</p>
                </div>
            </Container>
        );
    }

    // Error State
    if (purchaseError) {
        return (
            <Container>
                <div className={styles.errorContainer}>
                    <h2>Something Went Wrong</h2>
                    <Alert variant="danger">
                        Something went wrong while trying to fetch your codes...
                    </Alert>
                    <p>If you don't receive an email with your codes, check if you've been charged. Get in touch if you need support at: <a href="mailto:info@thegamescollective.com">info@thegamescollective.com</a></p>
                </div>
            </Container>
        );
    }

    // Redeemed View
    if (currentView === "redeemed") {
        return (
            <Container>
                <div className={styles.container}>
                    <div className={styles.redeemedCard}>
                        <div className={styles.redeemedIcon}>
                            <span>✓</span>
                        </div>
                        <h1 className={styles.redeemedTitle}>Redemption successful!</h1>
                        <p className={styles.redeemedSubtitle}>Your games have been added to your account.</p>

                        <div className={styles.redeemedAccountInfo}>
                            <span className={styles.redeemedAccountBadge}>Redeemed To</span>
                            <div className={styles.redeemedAccountContent}>
                                <div className={styles.redeemedAccountIcon}>
                                    <span>👤</span>
                                </div>
                                <span className={styles.redeemedAccountEmail}>{user?.email}</span>
                            </div>
                        </div>

                        {/*{activateMessage && (*/}
                        {/*    <Alert variant={activateStatus === "Success" ? "success" : activateStatus === "Error" ? "danger" : "warning"}>*/}
                        {/*        {activateMessage}*/}
                        {/*    </Alert>*/}
                        {/*)}*/}

                        <div className={styles.redeemedList}>
                            {products.map((product, idx) => (
                                <div key={idx} className={styles.redeemedRow}>
                                    <span className={styles.gameName}>{product.productName}</span>
                                    <span className={styles.redeemedStatus}>
                                        <span>✓</span> Added to account
                                    </span>
                                </div>
                            ))}
                        </div>

                        <button onClick={() => navigate("/lobby")} className={styles.hostButton}>
                            HOST GAME
                        </button>
                    </div>
                </div>
            </Container>
        );
    }

    // Voucher View
    if (currentView === "voucher") {
        return (
            <Container>
                <div className={styles.container}>
                    <div className={styles.voucherCard}>
                        <BackButton onClick={() => {
                            setCurrentView("confirmation")
                            window.scrollTo(0, 0)
                        }} />
                        <div className={styles.voucherIcon}>
                            <img src={presentIcon} alt="Gift" />
                        </div>
                        <h1 className={styles.voucherTitle}>Voucher Ready!</h1>
                        <p className={styles.voucherSubtitle}>Your gift voucher is on its way</p>

                        <div className={styles.emailConfirmation}>
                            <span className={styles.sentBadge}>Sent</span>
                            <div className={styles.confirmationContent}>
                                <div className={styles.confirmationIcon}>
                                    <span>✓</span>
                                </div>
                                <div className={styles.confirmationText}>
                                    <span className={styles.email}>{email}</span>
                                    <span className={styles.hint}>Download a copy below to share</span>
                                </div>
                            </div>
                        </div>

                        <button
                            className={styles.downloadButton}
                            onClick={handleDownloadPdf}
                            disabled={isDownloadingPdf}
                        >
                            {isDownloadingPdf ? (
                                <DotLoader size="medium" variant="light" />
                            ) : (
                                <>
                                    <span>📄</span>
                                    Download PDF
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </Container>
        );
    }

    // Confirmation View (Default)
    return (
        <Container>
            <div className={styles.container}>
                <div className={styles.confirmationCard}>
                    {/* Success Header */}
                    <div className={styles.successHeader}>
                        <div className={styles.successIcon}>
                            <span>✓</span>
                        </div>
                        <h1 className={styles.gradientTitle}>Purchase Complete!</h1>
                        <p className={styles.subtitle}>We've sent the codes and receipt to your inbox</p>
                    </div>

                    {/* Email Section */}
                    <div className={styles.emailSection}>
                        <span className={styles.emailBadge}>Delivery Email</span>
                        <div className={styles.emailDisplay}>
                            <div className={styles.emailIcon}>
                                <span>✉</span>
                            </div>
                            <span className={styles.emailAddress}>{email}</span>
                        </div>
                        <p className={styles.emailHint}>Wrong email? Update it below and we'll resend your codes and receipt immediately.</p>
                        <div className={styles.emailEditRow}>
                            <input
                                type="email"
                                value={editEmail}
                                onChange={(e) => setEditEmail(e.target.value)}
                                className={styles.emailInput}
                                placeholder="Enter new email"
                            />
                            <button
                                onClick={handleUpdateEmail}
                                disabled={isUpdatingEmail || !editEmail || editEmail === email}
                                className={`${styles.updateButton} ${emailUpdateStatus === "success" ? styles.success : ""}`}
                            >
                                {isUpdatingEmail ? <DotLoader size="small" variant="light" /> : emailUpdateStatus === "success" ? "RE-SENT" : "RE-SEND"}
                            </button>
                        </div>
                        {emailUpdateStatus === "error" && (
                            <p className={styles.emailError}>Failed to update email. Please try again.</p>
                        )}
                    </div>

                    {/* What's Next Section */}
                    <div className={styles.sectionHeader}>
                        <span className={styles.line}></span>
                        <span>What's Next?</span>
                        <span className={`${styles.line}`}></span>
                    </div>

                    {/* Action Cards */}
                    <div className={styles.actionCards}>
                        {/* Play Now / Add to Account */}
                        <div className={styles.actionCard} onClick={handlePlayNow}>
                            <div className={`${styles.actionIcon} ${styles.green}`}>
                                <span>▶</span>
                            </div>
                            <div className={styles.actionContent}>
                                <h3>{isLoggedIn ? "Add to Account" : "Play Now"}</h3>
                                <p>{isLoggedIn ? "Add games to your account and start playing" : "Create an account and start playing instantly"}</p>
                            </div>
                            <div className={styles.actionArrow}>
                                <span>→</span>
                            </div>
                        </div>

                        {/* Redeem Later */}
                        <div className={`${styles.actionCard} ${styles.noClick}`}>
                            <div className={`${styles.actionIcon} ${styles.blue}`}>
                                <img src={clockIcon} alt="Clock" />
                            </div>
                            <div className={styles.actionContent}>
                                <h3>Redeem Later</h3>
                                <p>Codes are in your email - redeem whenever you're ready</p>
                            </div>
                        </div>

                        {/* Gift Voucher */}
                        <div className={`${styles.actionCard} ${styles.purple}`} onClick={handleGiftVoucher}>
                            <div className={`${styles.actionIcon} ${styles.purple}`}>
                                <img src={presentIcon} alt="Gift" />
                            </div>
                            <div className={styles.actionContent}>
                                <h3>Purchased as a Gift?</h3>
                                <p>Get a beautiful voucher to share with friends & family</p>
                            </div>
                            <div className={styles.actionArrow}>
                                <span>{isActivating ? "..." : "→"}</span>
                            </div>
                        </div>
                    </div>

                    {/* Your Codes Section */}
                    <div className={styles.codesSection}>
                        <span className={styles.codesBadge}>Your Codes</span>
                        <div className={styles.codesHeader}>
                            <span className={styles.countBadge}>{getTotalCodesCount()} Code{getTotalCodesCount() !== 1 ? 's' : ''}</span>
                        </div>
                        <div className={styles.codesList}>
                            {products.map((product, productIdx) => (
                                product.codes.map((code, codeIdx) => (
                                    <div key={`${productIdx}-${codeIdx}`} className={styles.codeRow}>
                                        <span className={styles.gameName}>{product.productName}</span>
                                        <div className={styles.codeValue}>
                                            <code>{truncateCode(code)}</code>
                                            <button
                                                className={`${styles.copyButton} ${copiedCode === code ? styles.copied : ''}`}
                                                onClick={() => copyToClipboard(code)}
                                            >
                                                <span>{copiedCode === code ? '✓' : '📋'}</span>
                                            </button>
                                        </div>
                                    </div>
                                ))
                            ))}
                        </div>
                        <button
                            className={styles.codesDownloadButton}
                            onClick={handleDownloadPdf}
                            disabled={isDownloadingPdf}
                        >
                            {isDownloadingPdf ? <DotLoader size="small" variant="light" /> : <><span>📄</span> Download My Codes</>}
                        </button>
                    </div>
                </div>
            </div>

            {/* Activation Confirmation Modal */}
            <Modal show={showConfirmationModal} onHide={handleClose}>
                <div className={formStyles.activateModal}>
                    <h2>{missingGames.length > 0 ? "Missing Base Game" : "Activate"}</h2>
                    {missingGames.length > 0 && (
                        <div className={formStyles.balanced}>
                            {`You are missing ${missingGames.length === 1 ? "an" : "multiple"} add-on's base game${missingGames.length === 1 ? "" : "s"}:`}
                        </div>
                    )}
                    <ul>
                        {missingGames.map((p, idx) => (
                            <li key={idx}>
                                <p className={`${formStyles.bold} ${formStyles.link}`} onClick={() => window.open(p.storeSlug)}>
                                    {p.name}
                                </p>
                            </li>
                        ))}
                    </ul>
                    <div>Are you sure you want to activate these items?</div>
                    <ul>
                        {products.map((p, idx) => (
                            <li key={idx}><p className={formStyles.bold}>{p.productName}</p></li>
                        ))}
                    </ul>
                    {activateMessage.length > 0 && activateStatus === "danger" && (
                        <Alert variant={activateStatus === "Success" ? "success" : activateStatus === "Error" ? "danger" : "warning"}>
                            {activateMessage}
                        </Alert>
                    )}
                    <div className={formStyles.buttonWrapper}>
                        <button onClick={handleClose} className={formStyles.button}>Close</button>
                        <button onClick={() => clickActivate(false)} className={formStyles.button}>
                            {isActivating ? <DotLoader size="medium" variant="light" /> : "Activate"}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Login Modal */}
            <Modal show={showLoginModal} onHide={handleLoginClose} centered className="autoWidthModal">
                <FormPage is="login" modal={true}>
                    <Login modal={true} closeModal={handleLoginClose} variant="checkout" />
                </FormPage>
            </Modal>
        </Container>
    );
}
